// ActionButton.js
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfileContext } from '../contexts/UserProfileContext';
import { AccessContext } from '../contexts/AccessProvider';
import { useWorkflowCache } from '../contexts/WorkflowCacheContext';
import { closeTask } from '../services/api/tasks';

function ActionButton({ 
  action, 
  onNavigate, 
  disabled = false,
  fromStepId,
  actionIndex,
  // Close task specific props
  isCloseTask = false,
  taskId,
  accessKeyToken,
  onTaskClosed,
  afterClosePath = '/',
}) {
  const navigate = useNavigate();
  const { userProfile } = useContext(UserProfileContext);
  const access = useContext(AccessContext);
  const { assembleWorkReports, recordTransition } = useWorkflowCache();

  const handleClick = () => {
    if (disabled) return;
    
    if (isCloseTask) {
      handleCloseTask();
      return;
    }
    
    const { type, link } = action;
    
    switch (type) {
      case 'navigation':
        if (fromStepId && accessKeyToken && taskId) {
          recordTransition({
            accessKey: accessKeyToken,
            taskId,
            from: fromStepId,
            to: link,
            actionIndex,
            actionText: action.text || '',
          });
        }
        onNavigate(link);
        break;
      case 'external':
        window.open(link, '_blank');
        break;
      case 'apiCall':
        fetch(link, { method: 'GET' })
          .then((response) => {
            // handle success
            onNavigate(action.onSuccess || 'thankYou');
          })
          .catch((error) => {
            console.error(error);
            onNavigate(action.onError || 'errorView');
          });
        break;
      default:
        console.warn('Unknown action type:', type);
    }
  };

  const handleCloseTask = async () => {
    try {
      // Assemble work reports from cache
      const { work_reports } = assembleWorkReports({ accessKey: accessKeyToken, taskId });

      // Submit the work reports to the backend. Field engineer id is
      // optional for anonymous User Workflow visitors.
      const payload = { work_reports };
      if (userProfile?.field_engineer_id) {
        payload.field_engineer_id = userProfile.field_engineer_id;
      }
      await closeTask(accessKeyToken, taskId, payload);

      if (access?.fetchAssetDetails) {
        await access.fetchAssetDetails(accessKeyToken);
      }

      if (onTaskClosed) {
        onTaskClosed();
      }

      // The landing page has no other way of knowing a task was just closed,
      // and the submit itself is otherwise silent — the screen simply changes.
      navigate(afterClosePath, { state: { taskCompleted: true } });
    } catch (error) {
      console.error('Error closing task:', error);
      alert('An unexpected error occurred while closing the task.');
    }
  };

  const buttonText = isCloseTask ? 'Close Task' : action?.text || 'Submit';

  return (
    <button 
      className="btn btn-primary w-100" 
      onClick={handleClick}
      disabled={disabled}
    >
      {buttonText}
    </button>
  );
}

export default ActionButton;
